import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import "./About.css";

import { UilSearch, UilPlus, UilMinus } from "@iconscout/react-unicons";
import axios from "axios";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { resolveUrl } from "../../utils/storeUtils";
import logoHome from "../../imgs/logo 3.webp";
import Seo from "../../components/Seo/Seo";

/**
 * Turn CMS copy into readable blocks: respects blank lines, otherwise splits after the first full sentence.
 */
function getAboutStoryBlocks(raw) {
  if (!raw || typeof raw !== "string") return [];
  const normalized = raw.replace(/\r\n/g, "\n").trim();
  if (!normalized) return [];

  if (/\n\s*\n/.test(normalized)) {
    return normalized
      .split(/\n\s*\n+/)
      .map((p) => p.replace(/\n/g, " ").replace(/\s+/g, " ").trim())
      .filter(Boolean);
  }

  const oneLine = normalized.replace(/\n/g, " ").replace(/\s+/g, " ");
  const dot = oneLine.indexOf(". ");
  if (dot !== -1 && dot >= 32 && dot < oneLine.length - 48) {
    return [oneLine.slice(0, dot + 1).trim(), oneLine.slice(dot + 2).trim()];
  }

  return [oneLine];
}

/** Break very long blocks at sentence boundaries for scannability */
function reflowStoryBlocks(blocks, softMax = 400) {
  const out = [];
  for (const block of blocks) {
    if (block.length <= softMax) {
      out.push(block);
      continue;
    }
    let rest = block;
    while (rest.length > softMax) {
      let cut = -1;
      for (let i = Math.min(softMax, rest.length - 24); i < rest.length - 2; i++) {
        const c = rest[i];
        if ((c === "." || c === "!" || c === "?") && rest[i + 1] === " ") {
          cut = i;
          break;
        }
      }
      if (cut === -1) break;
      out.push(rest.slice(0, cut + 1).trim());
      rest = rest.slice(cut + 2).trim();
    }
    if (rest) out.push(rest);
  }
  return out.length ? out : blocks;
}

const AboutPage = () => {
  const [aboutData, setAboutData] = useState(null);
  const [projectsPreview, setProjectsPreview] = useState([]);
  const [projectsPreviewLoading, setProjectsPreviewLoading] = useState(true);
  const [t] = useTranslation("global");
  const [lang, setLang] = useState(localStorage.getItem("lang") || `"ar"`);

  useEffect(() => {
    const fetchAboutData = async () => {
      try {
        const response = await axios.get("/content/about");
        setAboutData(response?.data);
      } catch (error) {
        toast.error("Failed to fetch data. Please try again later.");
      }
    };

    fetchAboutData();

    const fetchProjectsPreview = async () => {
      try {
        setProjectsPreviewLoading(true);
        const response = await axios.get("/projects?page=1");
        const batch = response?.data?.projects ?? [];
        const withImages = batch.filter((p) => p?.image?.url);
        setProjectsPreview(withImages.slice(0, 8));
      } catch (error) {
        setProjectsPreview([]);
      } finally {
        setProjectsPreviewLoading(false);
      }
    };

    fetchProjectsPreview();

    const handleStorageChange = () => {
      setLang(localStorage.getItem("lang") || `"ar"`);
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  const storyBlocks = useMemo(() => {
    const text =
      lang === `"ar"` ? aboutData?.arMainText : aboutData?.mainText;
    return reflowStoryBlocks(getAboutStoryBlocks(text || t("header.about")));
  }, [lang, aboutData?.arMainText, aboutData?.mainText, t]);

  const scrollToStory = useCallback(() => {
    document.getElementById("about-story")?.scrollIntoView({ behavior: "smooth" });
  }, []);

  const heroPhotoUrl = `${process.env.PUBLIC_URL}/photos/about-wood-hero.webp`;

  return (
    <>
      <Seo
        title="من نحن"
        description="تعرّف على قصة الرملاء للصناعات الخشبية، قيمنا، ورحلتنا في تصميم وتصنيع أثاث خشبي عالي الجودة."
        path="/about"
      />
      <section
        className="aboutpage-hero-full"
        id="about-top"
        aria-labelledby="about-hero-title"
      >
        <div
          className="aboutpage-hero-full-photo"
          style={{ backgroundImage: `url("${heroPhotoUrl}")` }}
          aria-hidden="true"
        />
        <div className="aboutpage-hero-full-scrim" aria-hidden="true" />
        <div className="aboutpage-hero-full-vignette" aria-hidden="true" />
        <div className="container aboutpage-hero-full-inner">
          <p className="aboutpage-hero-kicker">{t("aboutpageHero.kicker")}</p>
          <h1 id="about-hero-title" className="aboutpage-hero-display">
            <span className="aboutpage-hero-brand">
              <img
                className="aboutpage-hero-logo"
                src={logoHome}
                alt={t("aboutpageHero.displayBrand")}
                loading="eager"
                decoding="async"
              />
            </span>
            <span className="aboutpage-hero-tagline">{t("aboutpageHero.displayTagline")}</span>
          </h1>
          <p className="aboutpage-hero-dek">{t("aboutpageHero.lead")}</p>
          <div className="aboutpage-hero-actions">
            <Link to="/contact" className="aboutpage-hero-cta aboutpage-hero-cta--primary">
              {t("aboutpageHero.cta")}
            </Link>
            <Link
              to="/projects"
              className="aboutpage-hero-cta aboutpage-hero-cta--ghost"
            >
              {t("aboutpageHero.secondaryCta")}
            </Link>
          </div>
        </div>
        <button
          type="button"
          className="aboutpage-hero-scroll"
          onClick={scrollToStory}
          aria-label={t("aboutpageHero.scrollAria")}
        >
          <i className="fa-solid fa-chevron-down" aria-hidden="true" />
        </button>
        <Link
          to="/contact"
          className="aboutpage-hero-mail"
          aria-label={t("aboutpageHero.ctaAria")}
        >
          <i className="fa-solid fa-envelope" aria-hidden="true" />
        </Link>
      </section>

      <section className="aboutpage-story-split" id="about-story">
        <div className="container aboutpage-story-split-grid">
          <aside
            className="aboutpage-story-split-aside"
            data-aos={lang === `"en"` ? "fade-right" : "fade-left"}
            data-aos-duration="900"
          >
            <span className="aboutpage-story-split-eyebrow">{t("aboutus.eyebrow")}</span>
            <h2 className="aboutpage-story-split-heading">{t("aboutpageStory.headline")}</h2>
            <div className="aboutpage-story-split-rule" aria-hidden="true" />
          </aside>
          <div
            className="aboutpage-story-split-body"
            data-aos={lang === `"en"` ? "fade-left" : "fade-right"}
            data-aos-duration="900"
            data-aos-delay="80"
          >
            {storyBlocks.map((block, i) => {
              const single = storyBlocks.length === 1;
              let cls = "aboutpage-split-p aboutpage-split-p--body";
              if (single) cls = "aboutpage-split-p aboutpage-split-p--single";
              else if (i === 0) cls = "aboutpage-split-p aboutpage-split-p--lead";
              return (
                <p key={i} className={cls}>
                  {block}
                </p>
              );
            })}
          </div>
        </div>
      </section>

      <section className="aboutpage-bento-section" aria-labelledby="about-bento-title">
        <div className="container">
          <header className="aboutpage-bento-head">
            <h2 id="about-bento-title">{t("aboutpageStory.galleryTitle")}</h2>
            <p>{t("aboutpageStory.gallerySubtitle")}</p>
          </header>
          {projectsPreviewLoading ? null : projectsPreview.length ? (
            <div className="aboutpage-bento">
              {projectsPreview.map((project, index) => {
                const name = lang === `"ar"` ? project?.arName : project?.name;
                const imgUrl = resolveUrl(project?.image?.url, axios.defaults.baseURL);
                const altText = project?.image?.alt || name || "";
                const searchName = name || "";
                const to =
                  searchName.trim().length > 0
                    ? `/projects?search=${encodeURIComponent(searchName)}`
                    : "/projects";

                return (
                  <Link
                    key={project?._id || project?.id || `about-project-${index}`}
                    to={to}
                    className={`aboutpage-bento-item aboutpage-bento-item--${index % 4}`}
                    aria-label={name || t("header.projects")}
                  >
                    <img src={imgUrl} alt={altText} loading="lazy" decoding="async" />
                    {name ? (
                      <div className="aboutpage-bento-caption" aria-hidden="true">
                        <span className="aboutpage-bento-caption-text">{name}</span>
                      </div>
                    ) : null}
                  </Link>
                );
              })}
            </div>
          ) : (
            <p className="aboutpage-bento-empty">
              {t("aboutpageStory.noProjects", {
                defaultValue: "No projects added yet.",
              })}
            </p>
          )}
        </div>
      </section>
      {/* <section className="art">
        <div className="container">
          <div className="last">
            <h1>The art of Branding</h1>
            <p>
              It is a long established fact that a reader will be distracted by
              the readable content of a page when looking at its layout. The
              point of using Lorem Ipsum is that it has a more-or-less normal
              distribution of letters, as opposed to using
            </p>
            <img src={circles} alt="before" />
          </div>
        </div>
      </section> */}
      <main className="about aboutpage-rest">
        <ThingsWeValue />
        <MakeDifferent />
      </main>
    </>
  );
};

function ThingsWeValue() {
  const [values, setValues] = useState([]);
  const [boxesToShow, setBoxesToShow] = useState(6);
  const [t] = useTranslation("global");
  const [lang, setLang] = useState(localStorage.getItem("lang") || `"ar"`);

  useEffect(() => {
    const fetchValues = async () => {
      try {
        const response = await axios.get("/content/values");
        setValues(response?.data);
      } catch (error) {}
    };

    fetchValues();

    const handleStorageChange = () => {
      setLang(localStorage.getItem("lang") || `"ar"`);
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  const handleToggleShowBoxes = () => {
    if (boxesToShow > 6) {
      setBoxesToShow(6);
    } else if (boxesToShow === 6) {
      setBoxesToShow(values?.length);
    }
  };

  if (!values?.length) {
    return null;
  }

  return (
    <section className="ThingsWeValue aboutpage-values">
      <div className="container aboutpage-values-inner">
        <header className="aboutpage-values-head">
          <h2
            className="aboutpage-values-title"
            data-aos={lang === `"en"` ? "fade-right" : "fade-left"}
            data-aos-duration="900"
          >
            {t("about.title")}
          </h2>
          <span className="aboutpage-values-hint">{t("aboutpageStory.valuesHint")}</span>
        </header>
        <div className="aboutpage-values-grid">
          {values?.slice(0, boxesToShow).map((value, index) => (
            <div
              className="aboutpage-value-tile"
              key={value?._id}
              data-aos="fade-up"
              data-aos-duration="1100"
              data-aos-delay={index * 120}
            >
              <span className="aboutpage-value-index" aria-hidden="true">
                {String(index + 1).padStart(2, "0")}
              </span>
              <div className="aboutpage-value-icon">
                <img
                  src={value?.image}
                  alt={lang === `"ar"` ? value?.arAlt : value?.alt}
                />
              </div>
              <h3>{lang === `"ar"` ? value?.arValue : value?.value}</h3>
            </div>
          ))}
        </div>
        {values?.length > 6 && (
          <button
            type="button"
            className="aboutpage-values-more"
            onClick={handleToggleShowBoxes}
          >
            {boxesToShow === 6
              ? t("aboutpageStory.valuesShowMore")
              : t("aboutpageStory.valuesShowLess")}
          </button>
        )}
      </div>
    </section>
  );
}

function MakeDifferent() {
  const [questions, setQuestions] = useState([]);
  const [boxesToShow, setBoxesToShow] = useState(6);
  const [visibleBoxes, setVisibleBoxes] = useState({});
  const [query, setQuery] = useState("");
  const [t] = useTranslation("global");
  const [lang, setLang] = useState(localStorage.getItem("lang") || `"ar"`);

  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        const response = await axios.get("/content/questions");
        setQuestions(response?.data);
      } catch (error) {}
    };

    fetchQuestions();

    const handleStorageChange = () => {
      setLang(localStorage.getItem("lang") || `"ar"`);
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  const handleToggleShowBoxes = () => {
    setBoxesToShow((prev) => (prev > 6 ? 6 : questions.length));
  };

  const toggleText = (index) => {
    setVisibleBoxes((prevVisibleBoxes) => ({
      ...prevVisibleBoxes,
      [index]: !prevVisibleBoxes[index],
    }));
  };

  const filteredQuestions = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return questions;

    return questions.filter((question) => {
      const title = (lang === `"ar"` ? question?.arTitle : question?.title) || "";
      const description =
        (lang === `"ar"` ? question?.arDescription : question?.description) || "";
      return (
        title.toLowerCase().includes(term) || description.toLowerCase().includes(term)
      );
    });
  }, [questions, query, lang]);

  return (
    <section className="MakeDifferent">
      <div className="container about-section-shell">
        <span className="about-section-eyebrow">{t("header.about")}</span>
        {questions?.length >= 1 && (
          <>
            <h2 className="title">{t("about.different")}</h2>
            <div className="search">
              <UilSearch />
              <input
                placeholder={t("about.search")}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
          </>
        )}
        <div className="boxs">
          {filteredQuestions?.slice(0, boxesToShow).map((question, index) => (
            <div
              className="box"
              key={question?._id}
              data-aos={lang === `"en"` ? "fade-right" : "fade-left"}
              data-aos-duration="1000"
              data-aos-delay={index * 100}
            >
              <div className="boxTitle" onClick={() => toggleText(index)}>
                <h3>{lang === `"ar"` ? question?.arTitle : question?.title}</h3>
                {visibleBoxes[index] ? (
                  <UilMinus size={35} />
                ) : (
                  <UilPlus size={35} />
                )}
              </div>
              <div className={`text-wrap ${visibleBoxes[index] ? "show" : ""}`}>
                <p className={`text ${visibleBoxes[index] ? "show" : ""}`}>
                  {lang === `"ar"`
                    ? question?.arDescription
                    : question?.description}
                </p>
              </div>
            </div>
          ))}
        </div>
        {filteredQuestions?.length > 6 && (
          <p className="show-more" onClick={handleToggleShowBoxes}>
            Show {boxesToShow === 6 ? "more" : "less"}
          </p>
        )}
      </div>
    </section>
  );
}

export default AboutPage;
